import React, { useState, useEffect } from 'react';
import { sanitizeRichHtml } from '../utils/sanitizeRichHtml';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  AppBar,
  Toolbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Menu,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as FileIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import {
  projectListPrimaryLabel,
  projectListSecondaryLabel,
  resolveProjectSponsorProtocolId,
} from '../utils/projectIdentityFields';
import documentEditorService from '../services/documentEditor.service';
import amendmentVersionService from '../services/amendmentVersion.service';
import summaryOfChangesService from '../services/summaryOfChanges.service';
import { generateContent } from '../services/AIService';
import { getPredefinedSections } from '../protocol/constants/sectionData';

// AI-powered highlighting system using LLM to detect changes
function DiffHighlight({ original, amended, changeContext = '' }) {
  const [highlightedContent, setHighlightedContent] = useState(amended);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    if (!original || !amended || original === amended) {
      setHighlightedContent(amended);
      return;
    }

    // Use AI to analyze and highlight changes
    const analyzeChanges = async () => {
      setIsAnalyzing(true);
      try {
        // Create a prompt for the AI to analyze changes
        const analysisPrompt = `
        Analyze the changes between the original and amended content.
        
        Original: ${original}
        Amended: ${amended}
        Change Context: ${changeContext}
        
        Please highlight the changed parts in the amended content using HTML spans:
        - Use <span style="background-color: #ffcdd2; color: #d32f2f; font-weight: bold;">text</span> for new/changed content
        - Use <span style="background-color: #fffde7; font-weight: bold;">text</span> for words mentioned in the change context
        - Only highlight the specific parts that changed, not entire lines
        - Return only the highlighted HTML content
        `;

        // Call AI service to analyze changes
        const response = await generateContent(analysisPrompt, '', '');
        
        if (response && response.trim()) {
          setHighlightedContent(response);
        } else {
          setHighlightedContent(amended);
        }
      } catch (error) {
        console.error('Error analyzing changes:', error);
        setHighlightedContent(amended);
      } finally {
        setIsAnalyzing(false);
      }
    };

    analyzeChanges();
  }, [original, amended, changeContext]);

  if (isAnalyzing) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px',
        padding: '8px',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px'
      }}>
        <div style={{ 
          width: '16px', 
          height: '16px', 
          border: '2px solid #ccc',
          borderTop: '2px solid #1976d2',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <span style={{ fontSize: '0.9rem', color: '#666' }}>
          Analyzing changes...
        </span>
      </div>
    );
  }

  return (
    <div 
      style={{ 
        whiteSpace: 'pre-line',
        lineHeight: '1.6'
      }}
      dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(highlightedContent) }}
    />
  );
}

// Legacy function for backward compatibility
function highlightDiff(original, amended, changeContext = '') {
  return <DiffHighlight original={original} amended={amended} changeContext={changeContext} />;
}

function normalizeTitle(str) {
  return (str || '')
    .toLowerCase()
    .replace(/[^a-z\s]/g, '') // remove non-letters
    .replace(/\s+/g, ' ') // normalize spaces
    .trim();
}



// Function to validate and clean generated content
function validateAndCleanContent(newContent, originalContent) {
  if (!newContent) return originalContent;
  
  // Check for problematic patterns
  const problematicPatterns = [
    /-{10,}/g, // Long sequences of dashes
    /_{10,}/g, // Long sequences of underscores
    /={10,}/g, // Long sequences of equals
    /\*{10,}/g, // Long sequences of asterisks
    /#{10,}/g, // Long sequences of hash symbols
    /placeholder|dummy|fake|test content/gi, // Placeholder text
    /\[.*?\]/g, // Square bracket placeholders
    /\(.*?\)/g, // Parentheses placeholders
  ];
  
  let cleanedContent = newContent;
  
  // Remove problematic patterns
  problematicPatterns.forEach(pattern => {
    cleanedContent = cleanedContent.replace(pattern, '');
  });
  
  // Check if content is too short or suspicious
  if (cleanedContent.trim().length < 10) {
    console.log('Generated content too short, using original');
    return originalContent;
  }
  
  // Check if content contains mostly special characters
  const specialCharRatio = (cleanedContent.match(/[-=_*#]/g) || []).length / cleanedContent.length;
  if (specialCharRatio > 0.3) {
    console.log('Generated content has too many special characters, using original');
    return originalContent;
  }
  
  // If content looks suspicious, return original
  if (cleanedContent.includes('Term/Abbreviation Definition') && 
      cleanedContent.includes('----------------------------------')) {
    console.log('Detected problematic glossary format, using original');
    return originalContent;
  }
  
  return cleanedContent.trim();
}

// Utility to highlight changed lines between two outputs
function getChangedLines(prev, curr) {
  if (!prev) return curr.split('\n').map((line, idx) => ({ line, changed: true, idx }));
  const prevLines = prev.split('\n');
  const currLines = curr.split('\n');
  return currLines.map((line, idx) => ({
    line,
    changed: prevLines[idx] !== line,
    idx
  }));
}

const AmendmentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const projectIdFromUrl = queryParams.get('projectId');
  
  const { currentProject, loading: projectContextLoading, error: projectContextError, loadProject } = useProject();
  const actualProjectId = currentProject?._id || projectIdFromUrl;

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewDialog, setPreviewDialog] = useState({ open: false, file: null });
  const [selectedDocumentType, setSelectedDocumentType] = useState('PROTOCOL');
  const [allSections, setAllSections] = useState([]);
  const [changeContext, setChangeContext] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [originalSections, setOriginalSections] = useState([]);
  const [noSectionsMatched, setNoSectionsMatched] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [amendmentHistory, setAmendmentHistory] = useState([]);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [tableModalData, setTableModalData] = useState([]);
  const [versionHistory, setVersionHistory] = useState([]);
  const [versionHistoryLoading, setVersionHistoryLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [editIdx, setEditIdx] = useState(-1);
  const [editRow, setEditRow] = useState({});
  const [tableModalEditData, setTableModalEditData] = useState([]);
  const [savedSummaryData, setSavedSummaryData] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [useSavedData, setUseSavedData] = useState(false);
  const [submittedVersions, setSubmittedVersions] = useState(new Set());
  const [exportAnchorEl, setExportAnchorEl] = useState(null);

  useEffect(() => {
    if (projectIdFromUrl && (!currentProject || currentProject._id !== projectIdFromUrl)) {
      loadProject(projectIdFromUrl);
    }
  }, [projectIdFromUrl, currentProject, loadProject]);

  useEffect(() => {
    if (actualProjectId && !projectContextLoading) {
      fetchSections();
      fetchVersionHistory();
      loadSavedSummaryData();
    }
  }, [actualProjectId, projectContextLoading, selectedDocumentType]);

  // When fetching protocol sections, also store originals
  useEffect(() => {
    if (selectedDocumentType === 'PROTOCOL' && actualProjectId && !projectContextLoading) {
      documentEditorService.getAllSections(actualProjectId).then(sections => {
        console.log('Setting original sections:', sections);
        setOriginalSections(sections);
      });
    }
  }, [selectedDocumentType, actualProjectId, projectContextLoading]);

  const fetchSections = async () => {
    try {
      setLoading(true);
      let response;
      
      // Use the appropriate service method based on document type
      switch (selectedDocumentType) {
        case 'PROTOCOL':
          response = await documentEditorService.getAllSections(actualProjectId);
          break;
        case 'SAP':
          response = await documentEditorService.getAllSAPSections(actualProjectId);
          break;
        case 'ICF':
          response = await documentEditorService.getAllICFSections(actualProjectId);
          break;
        default:
          response = await documentEditorService.getAllSections(actualProjectId);
      }
      
      console.log('Sections fetched for', selectedDocumentType, ':', response);
      setAllSections(response || []);
      setSections(response || []);
      
      // Store original sections for comparison when first loading
      if (selectedDocumentType === 'PROTOCOL' && (!originalSections || originalSections.length === 0)) {
        console.log('Storing original sections for comparison:', response);
        setOriginalSections(response || []);
      }
    } catch (err) {
      console.error('Error fetching sections:', err);
      setError(`Failed to fetch ${selectedDocumentType} sections`);
    } finally {
      setLoading(false);
    }
  };

  const fetchVersionHistory = async () => {
    if (!actualProjectId || !selectedDocumentType) return;
    
    try {
      setVersionHistoryLoading(true);
      const response = await amendmentVersionService.getAmendmentVersions(actualProjectId, selectedDocumentType);
      
      if (response.success) {
        setVersionHistory(response.data);
        
        // Track already submitted versions
        const submittedVers = new Set();
        response.data.forEach(version => {
          if (version.status === 'submitted') {
            submittedVers.add(version.versionNumber);
          }
        });
        setSubmittedVersions(submittedVers);
      } else {
        setError('Failed to fetch version history: ' + response.message);
      }
    } catch (err) {
      setError('Failed to fetch version history: ' + (err.message || err));
    } finally {
      setVersionHistoryLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'application/xml',
      'text/xml'
    ];

    const validFiles = files.filter(file => {
      if (allowedTypes.includes(file.type)) {
        return true;
      } else {
        setError(`File type not supported: ${file.name}`);
        return false;
      }
    });

    const newFiles = validFiles.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      type: file.type,
      size: file.size,
      file: file,
      uploadedAt: new Date().toISOString()
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);
    setSuccess(`${validFiles.length} file(s) uploaded successfully`);
  };

  const handleFileDelete = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
    setSuccess('File deleted successfully');
  };

  const handleFilePreview = (file) => {
    setPreviewDialog({ open: true, file });
  };

  const handleFileDownload = (file) => {
    const url = URL.createObjectURL(file.file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('xml')) return '📋';
    return '📄';
  };

  const getFileTypeLabel = (fileType) => {
    if (fileType.includes('pdf')) return 'PDF';
    if (fileType.includes('word') || fileType.includes('document')) return 'Word';
    if (fileType.includes('xml')) return 'XML';
    return 'Unknown';
  };

  const handleSaveAmendment = async () => {
    try {
      setLoading(true);
      
      // Get changed sections
      const changedSections = sections.filter(section => {
        const orig = originalSections.find(s => s._id === section._id);
        return orig && orig.output !== section.output;
      });

      if (changedSections.length === 0) {
        setError('No changes detected to save as amendment version');
        return;
      }

      // Create amendment version from changes
      const result = await amendmentVersionService.createAmendmentFromChanges(
        actualProjectId,
        selectedDocumentType,
        sections,
        originalSections
      );

      if (result.success) {
        setSuccess(`Amendment version ${result.data.versionNumber} created successfully!`);
        // Refresh version history
        fetchVersionHistory();
        // Reload sections to get updated content
        await reloadSections();
      } else {
        setError(result.message || 'Failed to save amendment version');
      }
    } catch (err) {
      setError('Failed to save amendment: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  // Function to reload sections and get updated version
  const reloadSections = async () => {
    try {
      setLoading(true);
      console.log('🔄 Reloading sections to get updated version...');
      
      // First, check if there are unsaved changes
      const changedSections = sections.filter(section => {
        const orig = originalSections.find(s => s._id === section._id);
        return orig && orig.output !== section.output;
      });

      // If there are unsaved changes, save them first
      if (changedSections.length > 0) {
        console.log('💾 Saving unsaved changes before reload...');
        
        const result = await amendmentVersionService.createAmendmentFromChanges(
          actualProjectId,
          selectedDocumentType,
          sections,
          originalSections
        );

        if (result.success) {
          setSuccess(`Changes saved as amendment version ${result.data.versionNumber} before reload`);
        } else {
          setError('Failed to save changes before reload: ' + (result.message || 'Unknown error'));
          return; // Don't reload if save failed
        }
      }
      
      // Fetch fresh sections from the server
      await fetchSections();
      
      // Also refresh version history
      await fetchVersionHistory();
      
      setSuccess('Sections reloaded successfully with latest updates!');
    } catch (err) {
      setError('Failed to reload sections: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const SYSTEM_INSTRUCTION = `System: You are a document editor. Your task is to update ONLY the specific parts of the section that are relevant to the user's change request. 

IMPORTANT RULES:
1. NEVER generate placeholder content, dashes, or repetitive characters
2. NEVER create fake or dummy content
3. If the change request doesn't apply to this section, return the original content unchanged
4. Only modify the specific text that needs to be changed based on the user's request
5. Keep all formatting, structure, and existing content intact
6. If you're unsure about a change, return the original content
7. Do not add any decorative elements, dashes, or placeholder text

Focus on making precise, targeted changes only where the user's request specifically applies.`;

  // Add function to load saved summary data
  const loadSavedSummaryData = async () => {
    try {
      setSummaryLoading(true);
      const result = await summaryOfChangesService.getSummaryOfChanges(actualProjectId, selectedDocumentType);
      
      if (result.success && result.data.table && result.data.table.length > 0) {
        setSavedSummaryData(result.data.table);
        setTableModalData(result.data.table);
        setUseSavedData(true); // Use saved data instead of generated
        console.log('Using saved summary data:', result.data.table);
      } else {
        setUseSavedData(false); // Use generated data
        console.log('No saved data found, will use generated data');
      }
    } catch (err) {
      console.error('Error loading saved summary data:', err);
      setError('Failed to load saved summary data: ' + (err.message || err));
      setUseSavedData(false); // Fallback to generated data
    } finally {
      setSummaryLoading(false);
    }
  };

  // Add function to save summary data
  const saveSummaryData = async (tableData) => {
    try {
      const result = await summaryOfChangesService.saveSummaryOfChanges(actualProjectId, selectedDocumentType, tableData);
      
      if (result.success) {
        setSuccess('Summary of changes saved successfully!');
        setSavedSummaryData(tableData);
        return true;
      } else {
        setError(result.message || 'Failed to save summary of changes');
        return false;
      }
    } catch (err) {
      console.error('Error saving summary data:', err);
      setError('Failed to save summary of changes: ' + (err.message || err));
      return false;
    }
  };

  // Function to save section changes immediately to database
  const saveSectionChanges = async (updatedSections) => {
    try {
      console.log('💾 Saving section changes to database...');
      
      // Save each changed section individually
      for (const section of updatedSections) {
        const orig = originalSections.find(s => s._id === section._id);
        if (orig && orig.output !== section.output) {
          // Save the section change with correct parameters
          const result = await documentEditorService.updateSection(
            section._id,
            {
              output: section.output,
              lastModified: new Date().toISOString()
            },
            actualProjectId // Add the projectId parameter
          );
          
          if (result.success) {
            console.log(`✅ Section "${section.title}" saved successfully`);
          } else {
            console.error(`❌ Failed to save section "${section.title}":`, result.message);
          }
        }
      }
      
      // Also create amendment version for tracking
      const changedSections = updatedSections.filter(section => {
        const orig = originalSections.find(s => s._id === section._id);
        return orig && orig.output !== section.output;
      });

      if (changedSections.length > 0) {
        const amendmentResult = await amendmentVersionService.createAmendmentFromChanges(
          actualProjectId,
          selectedDocumentType,
          updatedSections,
          originalSections
        );

        if (amendmentResult.success) {
          console.log(`✅ Amendment version ${amendmentResult.data.versionNumber} created`);
        }
      }
      
      setSuccess('Section changes saved to database successfully!');
    } catch (err) {
      console.error('Error saving section changes:', err);
      setError('Failed to save section changes: ' + (err.message || err));
    }
  };

  // Function to get all content for download
  const getAllContent = () => {
    if (!sections || sections.length === 0) {
      return '';
    }
    
    return sections.map(section => 
      `${section.title}\n${'='.repeat(section.title.length)}\n${section.output || ''}\n\n`
    ).join('');
  };

  // Download as PDF
  const downloadAsPDF = async (content, filename) => {
    try {
      // Import jsPDF dynamically
      const { jsPDF } = await import('jspdf');
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);
      
      // Split content into lines
      const lines = content.split('\n');
      let yPosition = margin;
      const lineHeight = 7;
      
      doc.setFontSize(12);
      doc.setFont('helvetica');
      
      lines.forEach((line, index) => {
        // Check if we need a new page
        if (yPosition > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          yPosition = margin;
        }
        
        // Handle long lines by wrapping text
        const wrappedLines = doc.splitTextToSize(line, maxWidth);
        
        wrappedLines.forEach(wrappedLine => {
          if (yPosition > doc.internal.pageSize.getHeight() - margin) {
            doc.addPage();
            yPosition = margin;
          }
          
          doc.text(wrappedLine, margin, yPosition);
          yPosition += lineHeight;
        });
      });
      
      // Save the PDF
      doc.save(`${filename}.pdf`);
      setSuccess('PDF downloaded successfully!');
    } catch (err) {
      throw new Error(`PDF generation failed: ${err.message}`);
    }
  };

  // Download as DOC
  const downloadAsDOC = async (content, filename) => {
    try {
      // Import docx dynamically
      const { Document, Packer, Paragraph, TextRun } = await import('docx');
      
      // Create document
      const doc = new Document({
        sections: [{
          properties: {},
          children: content.split('\n').map(line => 
            new Paragraph({
              children: [
                new TextRun({
                  text: line,
                  size: 24, // 12pt
                }),
              ],
            })
          ),
        }],
      });
      
      // Generate and download
      const blob = await Packer.toBlob(doc);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccess('DOC file downloaded successfully!');
    } catch (err) {
      throw new Error(`DOC generation failed: ${err.message}`);
    }
  };

  // Download as XML
  const downloadAsXML = async (content, filename) => {
    try {
      // Create XML structure
      const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<amendment>
  <metadata>
    <projectId>${actualProjectId}</projectId>
    <documentType>${selectedDocumentType}</documentType>
    <timestamp>${new Date().toISOString()}</timestamp>
    <changeContext>${changeContext}</changeContext>
  </metadata>
  <content>
    ${content.split('\n').map(line => 
      `<line>${line.replace(/[<>&'"]/g, (match) => {
        const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&apos;', '"': '&quot;' };
        return entities[match];
      })}</line>`
    ).join('\n    ')}
  </content>
</amendment>`;
      
      // Create and download file
      const blob = new Blob([xmlContent], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccess('XML file downloaded successfully!');
    } catch (err) {
      throw new Error(`XML generation failed: ${err.message}`);
    }
  };

  // Function to download content in different formats
  const downloadContent = async (format) => {
    try {
      setLoading(true);
      
      const content = getAllContent();
      if (!content.trim()) {
        setError('No content available for download');
        return;
      }
      
      const filename = `${currentProject?.name || 'Amendment'}_${selectedDocumentType}_${new Date().toISOString().split('T')[0]}`;
      
      switch (format.toLowerCase()) {
        case 'pdf':
          await downloadAsPDF(content, filename);
          break;
        case 'doc':
          await downloadAsDOC(content, filename);
          break;
        case 'xml':
          await downloadAsXML(content, filename);
          break;
        default:
          setError(`Unsupported format: ${format}`);
      }
    } catch (err) {
      setError(`Failed to download ${format}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Export dropdown handlers
  const handleExportMenuOpen = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportAnchorEl(null);
  };

  const handleExport = (format) => {
    handleExportMenuClose();
    downloadContent(format);
  };

  // Amendment features temporarily locked — only Back to Dashboard stays active
  const amendmentFeaturesLocked = true;
  if (amendmentFeaturesLocked) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            justifyContent: 'flex-start',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate('/clinical-intake?tab=home')}
            sx={{ textTransform: 'none' }}
          >
            Back to Dashboard
          </Button>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            py: 6,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
          >
            🚀 Launching awesomeness... please stand by.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4, maxWidth: 480 }}>
            Amendment management is not available yet. All features on this page are disabled.
          </Typography>

          {/* Greyed-out preview of amendment UI — non-interactive */}
          <Box
            sx={{
              width: '100%',
              maxWidth: 900,
              opacity: 0.4,
              filter: 'grayscale(0.75)',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
            aria-hidden
          >
            <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
                  Amendment Management
                </Typography>
                <Button variant="outlined" size="small" disabled>
                  Document Type
                </Button>
                <Button variant="contained" size="small" disabled startIcon={<SaveIcon />}>
                  Save Amendment
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip label="Upload" disabled size="small" />
                <Chip label="Amendment Editor" disabled size="small" />
                <Chip label="History" disabled size="small" />
              </Box>
              <Box
                sx={{
                  height: 160,
                  borderRadius: 1,
                  bgcolor: 'action.disabledBackground',
                  border: '1px dashed',
                  borderColor: 'divider',
                }}
              />
            </Paper>
          </Box>
        </Box>
      </Box>
    );
  }

  if (projectContextLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '300px',
        bgcolor: 'background.default'
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2, fontWeight: 500 }}>
          Loading Study Information...
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Please wait while we fetch the project details
        </Typography>
      </Box>
    );
  }

  if (projectContextError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{projectContextError}</Alert>
      </Box>
    );
  }

  if (!currentProject) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '300px',
        bgcolor: 'background.default',
        p: 3
      }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
          📋 No Study Selected
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 3, textAlign: 'center' }}>
          Please select a study/project to access the amendment management features.
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate('/clinical-intake?tab=home')}
          startIcon={<AddIcon />}
        >
          Go to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', bgcolor: 'background.default', minHeight: '100vh'}}>
      {/* Project Name Header */}
      {currentProject && (
        <Box sx={{ 
          bgcolor: 'primary.main', 
          color: 'white', 
          py: 3, 
          px: 3, 
          mt: 4,
          borderBottom: '2px solid',
          borderColor: 'primary.dark'
        }}>
          <Typography variant="h4" sx={{ fontWeight: 600, textAlign: 'center' }}>
            📋 Trial: {projectListPrimaryLabel(currentProject)}
          </Typography>
          {projectListSecondaryLabel(currentProject) && (
            <Typography variant="subtitle1" sx={{ textAlign: 'center', mt: 1, opacity: 0.9 }}>
              {projectListSecondaryLabel(currentProject)}
            </Typography>
          )}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: 3, 
            mt: 2,
            flexWrap: 'wrap'
          }}>
            {resolveProjectSponsorProtocolId(currentProject) && (
              <Chip 
                label={`Sponsor Protocol Identifier: ${resolveProjectSponsorProtocolId(currentProject)}`}
                variant="outlined"
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.1)', 
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.3)'
                }}
              />
            )}
            {currentProject.status && (
              <Chip 
                label={`Status: ${currentProject.status.toUpperCase()}`}
                variant="outlined"
                color={currentProject.status === 'active' ? 'success' : 'default'}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.1)', 
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.3)'
                }}
              />
            )}
          </Box>
        </Box>
      )}
      
      <AppBar 
        position="static" 
        elevation={0} 
        sx={{ 
          mt: currentProject ? 0 : 4,
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          borderBottom: '1px solid #dee2e6',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
      >
        <Toolbar sx={{ 
          minHeight: 64, 
          px: 3,
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap'
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 600,
              color: '#495057',
              fontSize: '1.1rem'
            }}
          >
            Amendment Management
          </Typography>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="document-type-label">Document Type</InputLabel>
            <Select
              labelId="document-type-label"
              id="document-type-select"
              value={selectedDocumentType}
              label="Document Type"
              onChange={(e) => setSelectedDocumentType(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'white',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6c757d'
                  }
                }
              }}
            >
              <MenuItem value="PROTOCOL">Protocol</MenuItem>
              <MenuItem value="SAP">SAP</MenuItem>
              <MenuItem value="ICF">ICF</MenuItem>
            </Select>
          </FormControl>
          <Button
            startIcon={<SaveIcon />}
            variant="contained"
            onClick={handleSaveAmendment}
            disabled={loading}
            sx={{ 
              px: 2,
              py: 0.8,
              minWidth: '120px',
              borderRadius: 2,
              fontWeight: 600,
              fontSize: '0.85rem',
              backgroundColor: '#28a745',
              '&:hover': {
                backgroundColor: '#218838',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)'
              },
              '&:disabled': {
                backgroundColor: '#6c757d'
              }
            }}
          >
            Save Amendment
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
            onClick={reloadSections}
            disabled={loading}
            title="Reload sections to get latest updates"
            sx={{ 
              px: 1.5,
              py: 0.6,
              minWidth: '90px',
              borderRadius: 2,
              fontWeight: 600,
              fontSize: '0.8rem',
              borderColor: '#6c757d',
              color: '#495057',
              '&:hover': {
                borderColor: '#495057',
                backgroundColor: '#f8f9fa',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(108, 117, 125, 0.2)'
              }
            }}
          >
            Reload
          </Button>
          
          {/* Export Dropdown Button */}
          <Button
            startIcon={<DownloadIcon />}
            variant="outlined"
            onClick={handleExportMenuOpen}
            disabled={loading || sections.length === 0}
            title="Export Document"
            sx={{ 
              px: 1.5,
              py: 0.6,
              minWidth: '90px',
              borderRadius: 2,
              fontWeight: 600,
              fontSize: '0.8rem',
              borderColor: '#6c757d',
              color: '#495057',
              '&:hover': {
                borderColor: '#495057',
                backgroundColor: '#f8f9fa',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(108, 117, 125, 0.2)'
              },
              '&:disabled': {
                borderColor: '#adb5bd',
                color: '#adb5bd'
              }
            }}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={handleExportMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                border: '1px solid #e9ecef',
                mt: 1
              }
            }}
          >
            <MenuItem 
              onClick={() => handleExport('pdf')}
              sx={{ 
                py: 1.5,
                px: 2,
                '&:hover': {
                  backgroundColor: '#f8f9fa'
                }
              }}
            >
              <DownloadIcon sx={{ mr: 1.5, color: '#dc3545' }} />
              Export as PDF
            </MenuItem>
            <MenuItem 
              onClick={() => handleExport('doc')}
              sx={{ 
                py: 1.5,
                px: 2,
                '&:hover': {
                  backgroundColor: '#f8f9fa'
                }
              }}
            >
              <DownloadIcon sx={{ mr: 1.5, color: '#007bff' }} />
              Export as DOC
            </MenuItem>
            <MenuItem 
              onClick={() => handleExport('xml')}
              sx={{ 
                py: 1.5,
                px: 2,
                '&:hover': {
                  backgroundColor: '#f8f9fa'
                }
              }}
            >
              <DownloadIcon sx={{ mr: 1.5, color: '#28a745' }} />
              Export as XML
            </MenuItem>
          </Menu>
          <Button
            variant="outlined"
            onClick={() => navigate('/clinical-intake?tab=home')}
            sx={{ 
              px: 1.5,
              py: 0.6,
              minWidth: '140px',
              borderRadius: 2,
              fontWeight: 600,
              fontSize: '0.8rem',
              borderColor: '#6c757d',
              color: '#495057',
              '&:hover': {
                borderColor: '#495057',
                backgroundColor: '#f8f9fa',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(108, 117, 125, 0.2)'
              }
            }}
          >
            Back to Dashboard
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Tabs for Amendment Management */}
        <Box sx={{ mb: 3 }}>
          <Tabs 
            value={currentTab} 
            onChange={(e, newValue) => setCurrentTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              mb: 3
            }}
          >
            <Tab label="Amendment Editor" />
            <Tab label="Version History" />
          </Tabs>
        </Box>

        {currentTab === 0 && (
          <Grid container spacing={3}>
          {/* File Upload Section */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: 'fit-content' }}>
              <Typography variant="h6" gutterBottom>
                Upload Amendment Documents
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Upload PDF, Word, or XML files for the amendment
              </Typography>
              
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                sx={{ mb: 2, width: '100%', py: 2 }}
              >
                Choose Files
                <input
                  type="file"
                  hidden
                  multiple
                  accept=".pdf,.doc,.docx,.xml"
                  onChange={handleFileUpload}
                />
              </Button>

              {/* Amendment Context Textbox - Only for Protocol */}
              {selectedDocumentType === 'PROTOCOL' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Describe the changes to apply to sections
                  </Typography>
                  <TextField
                    multiline
                    minRows={3}
                    maxRows={8}
                    fullWidth
                    placeholder="E.g. Change age to 18-65. Update dosage to 50mg. Add safety monitoring for all sections."
                    value={changeContext}
                    onChange={e => setChangeContext(e.target.value)}
                    variant="outlined"
                  />
                  
                  {/* Multi-Command Mode Description */}
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Multi-Command Mode:
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      Enter multiple commands separated by periods, semicolons, or new lines. The system will parse and apply each command intelligently to relevant sections.
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem' }}>
                      Example: "Change age to 18-65. Update dosage to 50mg. Add safety monitoring for all sections."
                      <br />
                      <strong>🔍 Highlighting:</strong> 
                      <span style={{ backgroundColor: '#ffcdd2', color: '#d32f2f', padding: '1px 2px', borderRadius: '2px', fontSize: '0.8rem' }}>Red</span> = new values, 
                      <span style={{ backgroundColor: '#fffde7', padding: '1px 2px', borderRadius: '2px', fontSize: '0.8rem' }}>Yellow</span> = specific words mentioned in prompt
                    </Typography>
                  </Box>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2, width: '100%' }}
                    disabled={!changeContext || isRegenerating}
                    onClick={async () => {
                      setIsRegenerating(true);
                      setNoSectionsMatched(false);
                      try {
                        // Fetch all protocol sections for the project
                        const protocolSections = await documentEditorService.getAllSections(actualProjectId);
                        // Get predefined sections for prompt matching
                        const predefinedSections = await getPredefinedSections(actualProjectId);
                        
                        let updatedCount = 0;
                        let regeneratedSections;
                        
                        // Multi-command mode
                        const commands = changeContext.split(/[.!?]+|\n+/).filter(cmd => cmd.trim().length > 0);
                        console.log('📝 Parsed commands:', commands);
                        
                        // Keywords that indicate "apply to all sections"
                        const allSectionKeywords = ['all', 'every', 'entire', 'whole', 'complete', 'global', 'universal', 'throughout', 'each'];
                        
                        regeneratedSections = await Promise.all(protocolSections.map(async (section) => {
                          let isRelevant = false;
                          let sectionSpecificContext = '';
                          
                          // Check each command
                          for (const command of commands) {
                            const commandLower = command.trim().toLowerCase();
                            const normalizedCommand = normalizeTitle(commandLower);
                            
                            // Check if this command is for all sections
                            const isAllSectionsCommand = allSectionKeywords.some(keyword => {
                              const patterns = [
                                new RegExp(`\\b${keyword}\\s+sections?\\b`, 'i'),
                                new RegExp(`\\b${keyword}\\s+section\\b`, 'i'),
                                new RegExp(`\\b${keyword}\\s+document\\b`, 'i'),
                                new RegExp(`\\b${keyword}\\s+content\\b`, 'i'),
                                new RegExp(`\\b${keyword}\\s+throughout\\b`, 'i'),
                                new RegExp(`\\b${keyword}\\s+section\\b`, 'i')
                              ];
                              return patterns.some(pattern => pattern.test(commandLower));
                            });
                            
                            if (isAllSectionsCommand) {
                              isRelevant = true;
                              sectionSpecificContext += (sectionSpecificContext ? '. ' : '') + command.trim();
                            } else {
                              // Check for section-specific commands
                              const normalizedTitle = normalizeTitle(section.title);
                              if (normalizedTitle && normalizedCommand.includes(normalizedTitle)) {
                                isRelevant = true;
                                sectionSpecificContext += (sectionSpecificContext ? '. ' : '') + command.trim();
                              }
                            }
                          }
                          
                          if (!isRelevant) {
                            return section;
                          }
                          
                          updatedCount++;
                          // Find the matching predefined section for the prompt
                          const predefined = predefinedSections.find(s => s.title === section.title);
                          const prompt = `${SYSTEM_INSTRUCTION}\n\n${predefined ? predefined.prompt : section.prompt || ''}`;
                          // Use the section-specific context
                          const newOutput = await generateContent(prompt, sectionSpecificContext || changeContext, section.output || '');
                          return {
                            ...section,
                            output: newOutput,
                            context: sectionSpecificContext || changeContext,
                          };
                        }));
                        
                        setSections(regeneratedSections);
                        setOriginalSections(protocolSections); // Save originals for diff
                        if (updatedCount === 0) setNoSectionsMatched(true);
                        
                        // Auto-save section changes to database
                        if (updatedCount > 0) {
                          console.log('Auto-saving section changes to database...');
                          saveSectionChanges(regeneratedSections);
                        }
                        
                        // Auto-save the summary table after regeneration
                        if (updatedCount > 0) {
                          console.log('Auto-saving summary table after section regeneration');
                          // Generate the summary table data
                          const changedSections = regeneratedSections.filter(section => {
                            const orig = protocolSections.find(s => s._id === section._id);
                            return orig && orig.output !== section.output;
                          });
                          
                          if (changedSections.length > 0) {
                            const summaryTableData = changedSections.map(section => {
                              const orig = protocolSections.find(s => s._id === section._id);
                              
                              // Extract only the changed lines for display
                              const getChangedLinesOnly = (originalContent, newContent) => {
                                if (!originalContent && !newContent) return { previous: 'No content', revised: 'No content' };
                                if (!originalContent) return { previous: 'No content', revised: newContent };
                                if (!newContent) return { previous: originalContent, revised: 'No content' };
                                
                                const originalLines = originalContent.split('\n');
                                const newLines = newContent.split('\n');
                                
                                // Find only the changed lines
                                const changedLines = [];
                                const maxLines = Math.max(originalLines.length, newLines.length);
                                
                                for (let i = 0; i < maxLines; i++) {
                                  const origLine = originalLines[i] || '';
                                  const newLine = newLines[i] || '';
                                  
                                  if (origLine !== newLine) {
                                    changedLines.push({
                                      lineNumber: i + 1,
                                      original: origLine,
                                      new: newLine,
                                      type: origLine && newLine ? 'changed' : origLine ? 'removed' : 'added'
                                    });
                                  }
                                }
                                
                                // Create display content with only changed lines
                                const previousContent = changedLines
                                  .filter(line => line.type === 'changed' || line.type === 'removed')
                                  .map(line => `Line ${line.lineNumber}: ${line.original}`)
                                  .join('\n');
                                
                                const revisedContent = changedLines
                                  .filter(line => line.type === 'changed' || line.type === 'added')
                                  .map(line => `Line ${line.lineNumber}: ${line.new}`)
                                  .join('\n');
                                
                                return {
                                  previous: previousContent || 'No changes detected',
                                  revised: revisedContent || 'No changes detected'
                                };
                              };
                              
                              const changedContent = getChangedLinesOnly(orig?.output || '', section.output || '');
                              
                              return {
                                section: section.title,
                                changeType: 'Content Update',
                                previousContent: changedContent.previous,
                                revisedContent: changedContent.revised,
                                rationale: section.context || changeContext || 'Amendment applied'
                              };
                            });
                            
                            saveSummaryData(summaryTableData).then(success => {
                              if (success) {
                                console.log('Auto-save after regeneration successful');
                                setSuccess(`Changes applied to ${updatedCount} sections and summary saved automatically!`);
                              } else {
                                console.log('Auto-save after regeneration failed');
                              }
                            });
                          }
                        }
                      } catch (err) {
                        setError('Failed to regenerate sections: ' + (err.message || err));
                      } finally {
                        setIsRegenerating(false);
                      }
                    }}
                  >
                    {isRegenerating ? 'Regenerating Sections...' : 'Apply Multi-Command Changes'}
                  </Button>
                </Box>
              )}

              {uploadedFiles.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Uploaded Files ({uploadedFiles.length})
                  </Typography>
                  <List dense>
                    {uploadedFiles.map((file) => (
                      <ListItem
                        key={file.id}
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          mb: 1,
                          backgroundColor: 'background.paper'
                        }}
                      >
                        <ListItemIcon>
                          <Typography variant="h6">{getFileIcon(file.type)}</Typography>
                        </ListItemIcon>
                        <ListItemText
                          primary={file.name}
                          secondary={`${getFileTypeLabel(file.type)} • ${(file.size / 1024).toFixed(1)} KB`}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleFilePreview(file)}
                            title="Preview"
                          >
                            <ViewIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleFileDownload(file)}
                            title="Download"
                          >
                            <DownloadIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleFileDelete(file.id)}
                            title="Delete"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

                            {/* Summary of Changes Section */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 600 }}>
                  📋 Summary of Changes
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Exact changes that will be saved as amendment versions
                </Typography>
                
                {sections.length > 0 ? (
                  <Box sx={{ 
                    border: '2px solid #ff1744', 
                    borderRadius: 2, 
                    p: 2,
                    backgroundColor: 'rgba(255, 23, 68, 0.02)',
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}>
                    {(() => {
                      // Debug logging to understand the comparison issue
                      console.log('Sections:', sections.length);
                      console.log('Original sections:', originalSections.length);
                      console.log('Use saved data:', useSavedData);
                      console.log('Saved summary data:', savedSummaryData);
                      
                      // If we have saved data, use it instead of generating
                      if (useSavedData && savedSummaryData.length > 0) {
                        console.log('Using saved summary data for table');
                        return (
                          <>
                            {/* Summary Table */}
                            <Box sx={{ mb: 3 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                  📊 Amendment Summary Table (Saved Data)
                                </Typography>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    setTableModalData(savedSummaryData);
                                    setTableModalEditData(savedSummaryData.map(row => ({ ...row })));
                                    setTableModalOpen(true);
                                  }}
                                  sx={{ 
                                    borderColor: '#1976d2', 
                                    color: '#1976d2',
                                    '&:hover': { borderColor: '#1565c0', backgroundColor: 'rgba(25, 118, 210, 0.1)' }
                                  }}
                                >
                                  📱 Full Screen View
                                </Button>
                              </Box>
                              <Box sx={{ 
                                border: '2px solid #1976d2', 
                                borderRadius: 2,
                                overflow: 'auto',
                                backgroundColor: 'rgba(25, 118, 210, 0.02)',
                                maxWidth: '100%',
                                cursor: 'pointer',
                                '&:hover': { borderColor: '#1565c0', backgroundColor: 'rgba(25, 118, 210, 0.05)' }
                              }}
                              onClick={() => {
                                setTableModalData(savedSummaryData);
                                setTableModalEditData(savedSummaryData.map(row => ({ ...row })));
                                setTableModalOpen(true);
                              }}
                              >
                                <Box sx={{ 
                                  minWidth: '800px',
                                  width: '100%'
                                }}>
                                  {/* Table Header */}
                                  <Box sx={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: '200px 150px 250px 250px 200px',
                                    borderBottom: '2px solid #1976d2',
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                    fontWeight: 600
                                  }}>
                                    <Box sx={{ p: 2, borderRight: '1px solid rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>Section</Box>
                                    <Box sx={{ p: 2, borderRight: '1px solid rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>Type of Change</Box>
                                    <Box sx={{ p: 2, borderRight: '1px solid rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>Previous Content (Brief)</Box>
                                    <Box sx={{ p: 2, borderRight: '1px solid rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>Revised Content (Brief)</Box>
                                    <Box sx={{ p: 2, fontSize: '0.9rem' }}>Rationale</Box>
                                  </Box>
                                  
                                  {/* Table Rows */}
                                  {savedSummaryData.map((change, idx) => (
                                    <Box key={idx} sx={{ 
                                      display: 'grid', 
                                      gridTemplateColumns: '200px 150px 250px 250px 200px',
                                      borderBottom: '1px solid rgba(25, 118, 210, 0.2)',
                                      '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' },
                                      backgroundColor: idx % 2 === 0 ? 'rgba(25, 118, 210, 0.02)' : 'transparent'
                                    }}>
                                      <Box sx={{ 
                                        p: 2, 
                                        borderRight: '1px solid rgba(25, 118, 210, 0.2)', 
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                        wordBreak: 'break-word'
                                      }}>
                                        {change.section}
                                      </Box>
                                      <Box sx={{ 
                                        p: 2, 
                                        borderRight: '1px solid rgba(25, 118, 210, 0.2)',
                                        fontSize: '0.85rem'
                                      }}>
                                        {change.changeType}
                                      </Box>
                                      <Box sx={{ 
                                        p: 2, 
                                        borderRight: '1px solid rgba(25, 118, 210, 0.2)', 
                                        fontSize: '0.8rem',
                                        wordBreak: 'break-word',
                                        fontFamily: 'monospace',
                                        whiteSpace: 'pre-line',
                                        backgroundColor: 'rgba(255, 193, 7, 0.1)',
                                        border: '1px solid rgba(255, 193, 7, 0.3)',
                                        borderRadius: '2px'
                                      }}>
                                        {change.previousContent}
                                      </Box>
                                      <Box sx={{ 
                                        p: 2, 
                                        borderRight: '1px solid rgba(25, 118, 210, 0.2)', 
                                        fontSize: '0.8rem',
                                        wordBreak: 'break-word',
                                        fontFamily: 'monospace',
                                        whiteSpace: 'pre-line',
                                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                        border: '1px solid rgba(76, 175, 80, 0.3)',
                                        borderRadius: '2px'
                                      }}>
                                        {change.revisedContent}
                                      </Box>
                                      <Box sx={{ 
                                        p: 2, 
                                        fontSize: '0.8rem',
                                        wordBreak: 'break-word'
                                      }}>
                                        {change.rationale}
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            </Box>
                          </>
                        );
                      }
                      
                      // Original logic for generating from sections
                      const changedSections = sections.filter(section => {
                        const orig = originalSections.find(s => s._id === section._id);
                        
                        // If no original sections available, assume all sections are changed
                        if (originalSections.length === 0) {
                          console.log(`No original sections available, marking ${section.title} as changed`);
                          return true;
                        }
                        
                        // More robust comparison
                        const origOutput = orig?.output || '';
                        const sectionOutput = section.output || '';
                        const isDifferent = origOutput.trim() !== sectionOutput.trim();
                        
                        console.log(`Comparing section ${section.title}:`, {
                          sectionId: section._id,
                          origId: orig?._id,
                          origOutputLength: origOutput.length,
                          sectionOutputLength: sectionOutput.length,
                          isDifferent: isDifferent
                        });
                        
                        return isDifferent;
                      });
                      
                      console.log('Changed sections count:', changedSections.length);
                      
                      if (changedSections.length === 0) {
                        return (
                          <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                            No changes detected yet. Apply changes using the context box above.
                          </Typography>
                        );
                      }
                      
                      // Use saved data if available, otherwise generate from sections
                      let changeTableData;
                      
                      if (useSavedData && savedSummaryData.length > 0) {
                        console.log('Using saved summary data for table');
                        changeTableData = savedSummaryData;
                      } else {
                        console.log('Generating table data from sections');
                        changeTableData = changedSections.map(section => {
                        const orig = originalSections.find(s => s._id === section._id);
                        const originalLines = (orig?.output || '').split('\n');
                        const newLines = (section.output || '').split('\n');
                        
                        // Calculate actual line differences
                        const changedLines = newLines.filter((line, idx) => {
                          return originalLines[idx] !== line || idx >= originalLines.length;
                        }).length;
                        
                        // Determine change type based on section title
                        const getChangeType = (title) => {
                          const lowerTitle = title.toLowerCase();
                          if (lowerTitle.includes('inclusion') || lowerTitle.includes('exclusion')) return 'Scientific/Eligibility';
                          if (lowerTitle.includes('dosage') || lowerTitle.includes('dose')) return 'Dosing';
                          if (lowerTitle.includes('safety') || lowerTitle.includes('adverse')) return 'Safety-related';
                          if (lowerTitle.includes('title') || lowerTitle.includes('version')) return 'Administrative';
                          return 'Content Update';
                        };
                        
                          // Extract content differences for display - only changed lines
                         const getContentDifferences = (originalContent, newContent) => {
                           if (!originalContent && !newContent) return { previous: 'No content', revised: 'No content' };
                           if (!originalContent) return { previous: 'No content', revised: newContent };
                           if (!newContent) return { previous: originalContent, revised: 'No content' };
                           
                           const originalLines = originalContent.split('\n');
                           const newLines = newContent.split('\n');
                           
                            // Find only the changed lines
                           const changedLines = [];
                           const maxLines = Math.max(originalLines.length, newLines.length);
                           
                           for (let i = 0; i < maxLines; i++) {
                             const origLine = originalLines[i] || '';
                             const newLine = newLines[i] || '';
                             
                             if (origLine !== newLine) {
                               changedLines.push({
                                 lineNumber: i + 1,
                                 original: origLine,
                                 new: newLine,
                                 type: origLine && newLine ? 'changed' : origLine ? 'removed' : 'added'
                               });
                             }
                           }
                           
                            // Create display content with only changed lines
                           const previousContent = changedLines
                             .filter(line => line.type === 'changed' || line.type === 'removed')
                             .map(line => `Line ${line.lineNumber}: ${line.original}`)
                             .join('\n');
                           
                           const revisedContent = changedLines
                             .filter(line => line.type === 'changed' || line.type === 'added')
                             .map(line => `Line ${line.lineNumber}: ${line.new}`)
                             .join('\n');
                           
                           return {
                             previous: previousContent || 'No changes detected',
                             revised: revisedContent || 'No changes detected'
                           };
                         };
                        
                                                 const contentDiff = getContentDifferences(orig?.output || '', section.output || '');
                         
                         return {
                           section: section.title,
                           changeType: getChangeType(section.title),
                           previousContent: contentDiff.previous,
                           revisedContent: contentDiff.revised,
                           rationale: changeContext || 'Amendment applied',
                           changedLines: changedLines,
                           originalContent: orig?.output || '',
                           newContent: section.output || ''
                         };
                      });
                        
                        // Auto-save the generated table data to database
                        if (changeTableData.length > 0) {
                          console.log('Auto-saving generated table data to database');
                          saveSummaryData(changeTableData).then(success => {
                            if (success) {
                              console.log('Auto-save successful');
                            } else {
                              console.log('Auto-save failed');
                            }
                          });
                        }
                      }
                      
                      return (
                        <>
                                                                                {/* Summary Table */}
                           <Box sx={{ mb: 3 }}>
                             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                               <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                📊 Amendment Summary Table (Generated)
                               </Typography>
                               <Button
                                 variant="outlined"
                                 size="small"
                                 onClick={() => {
                                   setTableModalData(changeTableData);
                                  setTableModalEditData(changeTableData.map(row => ({ ...row })));
                                   setTableModalOpen(true);
                                 }}
                                 sx={{ 
                                   borderColor: '#1976d2', 
                                   color: '#1976d2',
                                   '&:hover': { borderColor: '#1565c0', backgroundColor: 'rgba(25, 118, 210, 0.1)' }
                                 }}
                               >
                                 📱 Full Screen View
                               </Button>
                             </Box>
                             <Box sx={{ 
                               border: '2px solid #1976d2', 
                               borderRadius: 2,
                               overflow: 'auto',
                               backgroundColor: 'rgba(25, 118, 210, 0.02)',
                               maxWidth: '100%',
                               cursor: 'pointer',
                               '&:hover': { borderColor: '#1565c0', backgroundColor: 'rgba(25, 118, 210, 0.05)' }
                             }}
                             onClick={() => {
                               setTableModalData(changeTableData);
                              setTableModalEditData(changeTableData.map(row => ({ ...row })));
                               setTableModalOpen(true);
                             }}
                             >
                               <Box sx={{ 
                                 minWidth: '800px',
                                 width: '100%'
                               }}>
                                 {/* Table Header */}
                                 <Box sx={{ 
                                   display: 'grid', 
                                   gridTemplateColumns: '200px 150px 250px 250px 200px',
                                   borderBottom: '2px solid #1976d2',
                                   backgroundColor: '#1976d2',
                                   color: 'white',
                                   fontWeight: 600
                                 }}>
                                   <Box sx={{ p: 2, borderRight: '1px solid rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>Section</Box>
                                   <Box sx={{ p: 2, borderRight: '1px solid rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>Type of Change</Box>
                                   <Box sx={{ p: 2, borderRight: '1px solid rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>Previous Content (Brief)</Box>
                                   <Box sx={{ p: 2, borderRight: '1px solid rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>Revised Content (Brief)</Box>
                                   <Box sx={{ p: 2, fontSize: '0.9rem' }}>Rationale</Box>
                                 </Box>
                                 
                                 {/* Table Rows */}
                                 {changeTableData.map((change, idx) => (
                                   <Box key={idx} sx={{ 
                                     display: 'grid', 
                                     gridTemplateColumns: '200px 150px 250px 250px 200px',
                                     borderBottom: '1px solid rgba(25, 118, 210, 0.2)',
                                     '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' },
                                     backgroundColor: idx % 2 === 0 ? 'rgba(25, 118, 210, 0.02)' : 'transparent'
                                   }}>
                                   <Box sx={{ 
                                     p: 2, 
                                     borderRight: '1px solid rgba(25, 118, 210, 0.2)', 
                                     fontWeight: 600,
                                     fontSize: '0.85rem',
                                     wordBreak: 'break-word'
                                   }}>
                                     {change.section}
                                   </Box>
                                   <Box sx={{ 
                                     p: 2, 
                                     borderRight: '1px solid rgba(25, 118, 210, 0.2)',
                                     fontSize: '0.85rem'
                                   }}>
                                     {change.changeType}
                                   </Box>
                                   <Box sx={{ 
                                     p: 2, 
                                     borderRight: '1px solid rgba(25, 118, 210, 0.2)', 
                                     fontSize: '0.8rem',
                                     wordBreak: 'break-word',
                                     fontFamily: 'monospace',
                                     whiteSpace: 'pre-line',
                                     backgroundColor: 'rgba(255, 193, 7, 0.1)',
                                     border: '1px solid rgba(255, 193, 7, 0.3)',
                                     borderRadius: '2px'
                                   }}>
                                     {change.previousContent}
                                   </Box>
                                   <Box sx={{ 
                                     p: 2, 
                                     borderRight: '1px solid rgba(25, 118, 210, 0.2)', 
                                     fontSize: '0.8rem',
                                     wordBreak: 'break-word',
                                     fontFamily: 'monospace',
                                     whiteSpace: 'pre-line',
                                     backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                     border: '1px solid rgba(76, 175, 80, 0.3)',
                                     borderRadius: '2px'
                                   }}>
                                     {change.revisedContent}
                                   </Box>
                                   <Box sx={{ 
                                     p: 2, 
                                     fontSize: '0.8rem',
                                     wordBreak: 'break-word'
                                   }}>
                                     {change.rationale}
                                   </Box>
                                 </Box>
                               ))}
                             </Box>
                           </Box>
                           </Box>
                        </>
                      );
                    })()}
                  </Box>
                ) : (
                  <Box sx={{ 
                    border: '1px dashed #ccc', 
                    borderRadius: 2, 
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: '#f9f9f9'
                  }}>
                    <Typography variant="body2" color="textSecondary">
                      No sections loaded yet. Select a document type and apply changes to see the summary.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Sections Section - now wider */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, height: 'fit-content' }}>
              <Typography variant="h6" gutterBottom>
                Document Sections
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Review and edit sections from the original document
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Multiple commands will be parsed and applied intelligently to relevant sections. Commands can be separated by periods, semicolons, or new lines.
                <br />
                <strong>🔍 Highlighting System:</strong>
                <br />
                • <span style={{ backgroundColor: '#fffde7', padding: '1px 4px', borderRadius: '2px', fontWeight: 'bold' }}>Yellow background</span> = Changed content lines
                <br />
                • <span style={{ backgroundColor: '#ffcdd2', color: '#d32f2f', padding: '1px 4px', borderRadius: '2px', fontWeight: 'bold' }}>Red text</span> = New values from your prompt (e.g., "change age to 18" → "18" highlighted in red)
                <br />
                • <span style={{ backgroundColor: '#fffde7', padding: '1px 4px', borderRadius: '2px', fontWeight: 'bold' }}>Yellow text</span> = Words mentioned in your prompt (e.g., "change age to 18" → "age" highlighted in yellow)
              </Alert>
              {noSectionsMatched && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No section titles matched your change context. Please check your input or try a different phrase.
                </Alert>
              )}
              {saveSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Amendment versions saved successfully!
                </Alert>
              )}
              {sections.length > 0 && originalSections.length > 0 && (
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ 
                    mb: 2,
                    // 🔴 RED HIGHLIGHT: Visual indicator for save changed parts functionality
                    border: '2px solid #ff1744',
                    boxShadow: '0 0 10px rgba(255, 23, 68, 0.3)',
                    '&:hover': {
                      boxShadow: '0 0 15px rgba(255, 23, 68, 0.5)',
                    }
                  }}
                  disabled={isSaving}
                  onClick={async () => {
                    setIsSaving(true);
                    setSaveSuccess(false);
                    try {
                      // Save only amended sections (output differs from original)
                      // This filters to save only sections that have been modified
                      // 🔴 RED HIGHLIGHT: Core functionality for saving only changed parts
                      const amendedSections = sections.filter(section => {
                        const orig = originalSections.find(s => s._id === section._id);
                        return orig && orig.output !== section.output;
                      });
                      
                      if (amendedSections.length === 0) {
                        setError('No changes detected to save as amendment version');
                        return;
                      }

                      // Use the new amendment version service
                      const result = await amendmentVersionService.createAmendmentFromChanges(
                        actualProjectId,
                        selectedDocumentType,
                        sections,
                        originalSections
                      );

                      if (result.success) {
                        setSaveSuccess(true);
                        setSuccess(`Amendment version ${result.data.versionNumber} created successfully!`);
                        // Refresh version history
                        fetchVersionHistory();
                        // Reload sections to get updated content
                        await reloadSections();
                      } else {
                        setError(result.message || 'Failed to save amendment version');
                      }
                    } catch (err) {
                      setError('Failed to save amendment versions: ' + (err.message || err));
                    } finally {
                      setIsSaving(false);
                    }
                  }}
                >
                  {isSaving ? 'Saving...' : '🔴 Save as Amendment Version (Changed Parts Only)'}
                </Button>
              )}
              {/* Amendment History Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={async () => {
                    setHistoryOpen(true);
                    setHistoryLoading(true);
                    setAmendmentHistory([]);
                    try {
                      // Fetch all protocol sections for the project
                      const protocolSections = await documentEditorService.getAllSections(actualProjectId);
                      // For each section, fetch all versions
                      const allVersions = await Promise.all(protocolSections.map(async (section) => {
                        const response = await documentEditorService.getAllVersions(section._id, actualProjectId);
                        const versions = response?.data?.versions || [];
                        return versions.map(v => ({ ...v, sectionTitle: section.title }));
                      }));
                      // Flatten
                      setAmendmentHistory(allVersions.flat());
                    } catch (err) {
                      setError('Failed to fetch amendment history: ' + (err.message || err));
                    } finally {
                      setHistoryLoading(false);
                    }
                  }}
                >
                  Amendment History
                </Button>
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : sections.length > 0 ? (
                <Box>
                  {sections.map((section, idx) => (
                    <Accordion key={section._id || idx} defaultExpanded sx={{ mb: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {section.title}
                        </Typography>
                        <Chip
                          label={section.documentType || 'PROTOCOL'}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ ml: 2 }}
                        />
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const documentType = selectedDocumentType.toLowerCase();
                              navigate(`/document-editor/${documentType}/${actualProjectId}?projectId=${actualProjectId}&section=${section._id}`);
                            }}
                            title="Edit Section"
                          >
                            <EditIcon />
                          </IconButton>
                        </Box>
                        <Typography
                          variant="body1"
                          color="textPrimary"
                          sx={{
                            whiteSpace: 'pre-line',
                            fontSize: '1.05rem',
                            lineHeight: 1.7,
                            p: 1,
                          }}
                          component="div"
                        >
                          {(() => {
                            const orig = originalSections.find(s => s._id === section._id);
                            return highlightDiff(orig?.output, section.output, changeContext);
                          })()}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="textSecondary">
                    No sections found for {selectedDocumentType} document type
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Try switching to a different document type or create sections for this document type first.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
        )}

        {currentTab === 1 && (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ color: '#1976d2', fontWeight: 600 }}>
              📋 Version History
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              View all amendment versions for this project
            </Typography>

            {versionHistoryLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : versionHistory.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No Amendment Versions Found
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  No amendment versions have been created yet. Make changes and save them to create version history.
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {versionHistory.map((version, index) => (
                  <Grid item xs={12} md={6} lg={4} key={version._id}>
                    <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          Version {version.versionNumber}
                        </Typography>
                        <Chip 
                          label={version.status}
                          color={version.status === 'approved' ? 'success' : version.status === 'rejected' ? 'error' : 'default'}
                          size="small"
                        />
                      </Box>
                      
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        {new Date(version.amendmentDate).toLocaleString()}
                      </Typography>
                      
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        <strong>Summary:</strong> {version.summary.changeDescription}
                      </Typography>
                      
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        <strong>Changes:</strong> {version.summary.totalSectionsChanged} sections, {version.summary.totalLinesChanged} lines
                      </Typography>
                      
                      <Box sx={{ mt: 'auto', display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setSelectedVersion(version);
                            setVersionModalOpen(true);
                          }}
                        >
                          View Details
                        </Button>
                        <Button
                          variant={submittedVersions.has(version.versionNumber) ? "contained" : "outlined"}
                          size="small"
                          color={submittedVersions.has(version.versionNumber) ? "success" : "primary"}
                          disabled={submittedVersions.has(version.versionNumber)}
                          onClick={async () => {
                            try {
                              // Add to submitted versions set immediately to disable button
                              setSubmittedVersions(prev => new Set([...prev, version.versionNumber]));
                              
                              await amendmentVersionService.updateAmendmentStatus(
                                actualProjectId,
                                selectedDocumentType,
                                version.versionNumber,
                                'submitted'
                              );
                              fetchVersionHistory();
                              setSuccess(`Version ${version.versionNumber} status updated to submitted`);
                            } catch (err) {
                              // Remove from submitted versions if request fails
                              setSubmittedVersions(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(version.versionNumber);
                                return newSet;
                              });
                              setError('Failed to update version status: ' + (err.message || err));
                            }
                          }}
                          startIcon={submittedVersions.has(version.versionNumber) ? <CheckCircleIcon /> : null}
                        >
                          {submittedVersions.has(version.versionNumber) ? 'Submitted' : 'Submit'}
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}
      </Box>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, file: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          File Preview: {previewDialog.file?.name}
        </DialogTitle>
        <DialogContent>
          {previewDialog.file && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h1" sx={{ mb: 2 }}>
                {getFileIcon(previewDialog.file.type)}
              </Typography>
              <Typography variant="h6" gutterBottom>
                {previewDialog.file.name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {getFileTypeLabel(previewDialog.file.type)} • {(previewDialog.file.size / 1024).toFixed(1)} KB
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                File preview not available. Use download to view the file.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog({ open: false, file: null })}>
            Close
          </Button>
          {previewDialog.file && (
            <Button
              variant="contained"
              onClick={() => handleFileDownload(previewDialog.file)}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Full Screen Table Modal */}
      <Dialog 
        open={tableModalOpen} 
        onClose={() => setTableModalOpen(false)} 
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '95vw',
            height: '90vh',
            maxWidth: 'none',
            margin: '2.5vh auto'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#1976d2', 
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            📊 Amendment Summary Table - Full Screen View
          </Typography>
          <IconButton
            onClick={() => setTableModalOpen(false)}
            sx={{ color: 'white' }}
          >
            <DeleteIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ 
            height: '100%', 
            overflow: 'auto',
            backgroundColor: 'rgba(25, 118, 210, 0.02)'
          }}>
            <Box sx={{ 
              minWidth: '100%',
              padding: 2
            }}>
              {/* Table Header */}
                               <Box sx={{ 
                   display: 'grid', 
                   gridTemplateColumns: '250px 180px 300px 300px 250px',
                   borderBottom: '2px solid #1976d2',
                   backgroundColor: '#1976d2',
                   color: 'white',
                   fontWeight: 600,
                   position: 'sticky',
                   top: 0,
                   zIndex: 1
                 }}>
                <Box sx={{ p: 3, borderRight: '1px solid rgba(255,255,255,0.3)', fontSize: '1rem' }}>Section</Box>
                <Box sx={{ p: 3, borderRight: '1px solid rgba(255,255,255,0.3)', fontSize: '1rem' }}>Type of Change</Box>
                <Box sx={{ p: 3, borderRight: '1px solid rgba(255,255,255,0.3)', fontSize: '1rem' }}>Previous Content (Brief)</Box>
                <Box sx={{ p: 3, borderRight: '1px solid rgba(255,255,255,0.3)', fontSize: '1rem' }}>Revised Content (Brief)</Box>
                <Box sx={{ p: 3, fontSize: '1rem' }}>Rationale</Box>
              </Box>
              
              {/* Table Rows */}
              {tableModalEditData.map((change, idx) => (
                                 <Box key={idx} sx={{ 
                   display: 'grid', 
                   gridTemplateColumns: '250px 180px 300px 300px 250px',
                   borderBottom: '1px solid rgba(25, 118, 210, 0.2)',
                   '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' },
                   backgroundColor: idx % 2 === 0 ? 'rgba(25, 118, 210, 0.02)' : 'transparent'
                 }}>
                                     <Box sx={{ 
                     p: 3, 
                     borderRight: '1px solid rgba(25, 118, 210, 0.2)', 
                     fontWeight: 600,
                     fontSize: '1rem',
                     wordBreak: 'break-word'
                   }}>
                     {change.section}
                   </Box>
                   <Box sx={{ 
                     p: 3, 
                     borderRight: '1px solid rgba(25, 118, 210, 0.2)',
                     fontSize: '1rem'
                   }}>
                     {change.changeType}
                   </Box>
                                                          <Box sx={{ 
                       p: 3, 
                       borderRight: '1px solid rgba(25, 118, 210, 0.2)', 
                       fontSize: '0.95rem',
                       wordBreak: 'break-word',
                       fontFamily: 'monospace',
                       whiteSpace: 'pre-line',
                       backgroundColor: 'rgba(255, 193, 7, 0.1)',
                       border: '1px solid rgba(255, 193, 7, 0.3)',
                       borderRadius: '4px'
                     }}>
                       {change.previousContent}
                     </Box>
                     <Box sx={{ 
                       p: 3, 
                       borderRight: '1px solid rgba(25, 118, 210, 0.2)', 
                       fontSize: '0.95rem',
                       wordBreak: 'break-word',
                       fontFamily: 'monospace',
                       whiteSpace: 'pre-line',
                       backgroundColor: 'rgba(76, 175, 80, 0.1)',
                       border: '1px solid rgba(76, 175, 80, 0.3)',
                       borderRadius: '4px'
                     }}>
                       {change.revisedContent}
                     </Box>
                  <Box sx={{ 
                    p: 3, 
                    fontSize: '0.95rem',
                    wordBreak: 'break-word'
                  }}>
                    <TextField value={change.rationale} onChange={e => {
                      const updated = [...tableModalEditData];
                      updated[idx].rationale = e.target.value;
                      setTableModalEditData(updated);
                    }} size="small" fullWidth multiline minRows={2} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={async () => {
            const success = await saveSummaryData(tableModalEditData);
            if (success) {
              setTableModalData(tableModalEditData);
              setTableModalOpen(false);
            }
          }}>
            Save All Changes
          </Button>
          <Button onClick={() => setTableModalOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version Details Modal */}
      <Dialog open={versionModalOpen} onClose={() => setVersionModalOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ 
          backgroundColor: '#1976d2', 
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            📋 Amendment Version {selectedVersion?.versionNumber} Details
          </Typography>
          <IconButton
            onClick={() => setVersionModalOpen(false)}
            sx={{ color: 'white' }}
          >
            <DeleteIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedVersion ? (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Version Information
                  </Typography>
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Version Number:</strong> {selectedVersion.versionNumber}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Date Created:</strong> {new Date(selectedVersion.amendmentDate).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Status:</strong> 
                      <Chip 
                        label={selectedVersion.status}
                        color={selectedVersion.status === 'approved' ? 'success' : selectedVersion.status === 'rejected' ? 'error' : 'default'}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Summary:</strong> {selectedVersion.summary.changeDescription}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Changes:</strong> {selectedVersion.summary.totalSectionsChanged} sections, {selectedVersion.summary.totalLinesChanged} lines
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Change Details
                  </Typography>
                  <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                    {selectedVersion.changes.map((change, index) => (
                      <Paper key={index} sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          {change.sectionTitle}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                          Type: {change.changeType}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Rationale:</strong> {change.rationale}
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            Content Changes:
                          </Typography>
                          <Box sx={{ 
                            backgroundColor: '#f5f5f5', 
                            p: 1, 
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            maxHeight: '200px',
                            overflow: 'auto'
                          }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                              {change.originalContent !== change.revisedContent ? 
                                `Original: ${change.originalContent}\n\nRevised: ${change.revisedContent}` :
                                'No content changes detected'
                              }
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No version details available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Amendment History Dialog */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Amendment History</DialogTitle>
        <DialogContent dividers>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : amendmentHistory.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No amendment versions found for this project.
            </Typography>
          ) : (
            <List>
              {amendmentHistory.map((version, idx) => (
                <ListItem
                  key={version._id || idx}
                  button
                  onClick={() => setSelectedHistory(version)}
                  sx={{ mb: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}
                >
                  <ListItemText
                    primary={version.sectionTitle || 'Untitled Section'}
                    secondary={
                      <>
                        <span>Date: {version.timestamp ? new Date(version.timestamp).toLocaleString() : 'N/A'}</span>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
          {/* Version Content Dialog */}
          <Dialog open={!!selectedHistory} onClose={() => setSelectedHistory(null)} maxWidth="md" fullWidth>
            <DialogTitle>
              {selectedHistory?.sectionTitle || 'Section'} - Version Content
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line', background: '#f9fbe7', p: 2, borderRadius: 1 }} component="div">
                {(() => {
                  // Find previous version for this section
                  if (!selectedHistory) return null;
                  const allForSection = amendmentHistory.filter(v => v.sectionTitle === selectedHistory.sectionTitle);
                  const sorted = allForSection.sort((a, b) => (a.versionNumber || 0) - (b.versionNumber || 0));
                  const idx = sorted.findIndex(v => v.versionNumber === selectedHistory.versionNumber);
                  const prev = idx > 0 ? sorted[idx - 1].output : '';
                  const curr = selectedHistory.output || '';
                  const lines = getChangedLines(prev, curr);
                  return lines.map(({ line, changed, idx }) =>
                    <div key={idx} style={changed ? { background: '#fffde7', fontWeight: 500 } : {}}>{line}</div>
                  );
                })()}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedHistory(null)}>Close</Button>
            </DialogActions>
          </Dialog>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AmendmentPage; 